import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  Card,
  DateField,
  EmptyState,
  Field,
  FreshnessBadge,
  PageHeader,
  Skeleton,
  Table,
  badgeToneForStatus,
} from "@evolua/ui";
import { useState, type FormEvent } from "react";
import { QueryErrorState } from "../api/QueryErrorState.js";
import {
  useCancelMeVacation,
  useMeVacationBalance,
  useMeVacationHistory,
  useMeVacationRequests,
  useRequestMeVacation,
} from "../api/use-me.js";
import { useSession } from "../session/SessionContext.js";

const HISTORY_LABEL: Record<string, string> = {
  TAKEN: "Tirado",
  SCHEDULED: "Agendado",
  SOLD: "Vendido",
};

export function FeriasPage() {
  const { session } = useSession();
  const balance = useMeVacationBalance();
  const history = useMeVacationHistory();
  const requests = useMeVacationRequests();
  const request = useRequestMeVacation();
  const cancel = useCancelMeVacation();

  const canRequest = Boolean(session?.permissions.includes("vacation:request"));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    try {
      await request.mutateAsync({
        startDate,
        endDate,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      });
      setStartDate("");
      setEndDate("");
      setReason("");
    } catch (err) {
      const message =
        err instanceof EvoluaApiError
          ? err.problem.detail
          : err instanceof Error
            ? err.message
            : "Falha ao solicitar";
      setFormError(message);
    }
  }

  return (
    <>
      <PageHeader
        title="Férias"
        lead="Saldo do ERP, solicitação com workflow de aprovação e cancelamento antes da decisão (FASE-06-T35/T36)."
      />

      {balance.isPending ? (
        <Skeleton height="6rem" label="Carregando saldo de férias" />
      ) : balance.isError || !balance.data ? (
        <QueryErrorState error={balance.error} onRetry={() => void balance.refetch()} />
      ) : balance.data.periods.length === 0 ? (
        <EmptyState
          title="Sem períodos"
          description="Nenhum saldo de férias disponível."
        />
      ) : (
        <div className="evolua-stack evolua-stack--3">
          {balance.data.periods.map((period) => (
            <Card key={period.id} title={period.label}>
              <p>{period.balanceDays} dias</p>
              <p>
                <Badge tone={badgeToneForStatus(period.status)}>{period.status}</Badge>
              </p>
              <p>
                Aquisitivo {period.acquisitiveStart} → {period.acquisitiveEnd}
              </p>
              <p>Limite para gozo: {period.limitDate}</p>
              <FreshnessBadge syncedAt={balance.data.syncedAt ?? null} />
            </Card>
          ))}
        </div>
      )}

      {canRequest ? (
        <Card title="Solicitar férias">
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="evolua-stack evolua-stack--3"
          >
            <DateField
              label="Início"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <DateField
              label="Fim"
              required
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <Field
              label="Motivo (opcional)"
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
            {formError ? <Alert tone="danger">{formError}</Alert> : null}
            <Button type="submit" disabled={request.isPending}>
              Enviar para o gestor
            </Button>
          </form>
        </Card>
      ) : null}

      <h2 className="evolua-section-title">Minhas solicitações</h2>
      {requests.isPending ? (
        <Skeleton height="4rem" label="Carregando solicitações" />
      ) : requests.isError ? (
        <QueryErrorState error={requests.error} onRetry={() => void requests.refetch()} />
      ) : (requests.data?.data.length ?? 0) === 0 ? (
        <EmptyState
          title="Nenhuma solicitação"
          description="Quando você pedir férias, o status aparece aqui."
        />
      ) : (
        <Table
          caption="Minhas solicitações"
          getRowId={(row) => row.id}
          columns={[
            {
              id: "status",
              header: "Status",
              cell: (row) => (
                <Badge tone={badgeToneForStatus(row.status)}>{row.status}</Badge>
              ),
            },
            {
              id: "period",
              header: "Período",
              cell: (row) => {
                const payload = row.payload as unknown as {
                  startDate?: string;
                  endDate?: string;
                  days?: number;
                };
                return `${payload.startDate} a ${payload.endDate} (${payload.days ?? "?"} dias)`;
              },
            },
            {
              id: "actions",
              header: "Ações",
              cell: (row) =>
                row.status === "IN_PROGRESS" && canRequest ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={cancel.isPending}
                    onClick={() => void cancel.mutateAsync(row.id)}
                  >
                    Cancelar
                  </Button>
                ) : (
                  "—"
                ),
            },
          ]}
          rows={requests.data!.data}
        />
      )}

      <h2 className="evolua-section-title">Histórico</h2>
      {history.isPending ? (
        <Skeleton height="4rem" label="Carregando histórico" />
      ) : history.isError || !history.data ? (
        <QueryErrorState error={history.error} onRetry={() => void history.refetch()} />
      ) : history.data.data.length === 0 ? (
        <EmptyState
          title="Sem histórico"
          description="Nenhum registro de férias ainda."
        />
      ) : (
        <Table
          caption="Histórico"
          getRowId={(entry) => entry.id}
          columns={[
            {
              id: "type",
              header: "Tipo",
              cell: (entry) => HISTORY_LABEL[entry.type] ?? entry.type,
            },
            {
              id: "period",
              header: "Período",
              cell: (entry) =>
                `${entry.startDate} a ${entry.endDate} (${entry.days} dias)`,
            },
            {
              id: "status",
              header: "Status",
              cell: (entry) => (
                <Badge tone={badgeToneForStatus(entry.status)}>{entry.status}</Badge>
              ),
            },
          ]}
          rows={history.data.data}
        />
      )}
    </>
  );
}
